import { useEffect, useRef } from 'react'

const clientId = import.meta.env.VITE_GOOGLE_WEB_CLIENT_ID as string | undefined

/** Same visual style as the Login page Facebook (secondary) button. */
const buttonClass =
  'w-full bg-black/40 border border-gray-700 hover:bg-black/60 font-medium inline-flex items-center justify-center rounded-md text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-black py-2 px-4'

interface GoogleSignInButtonProps {
  onSuccess: (credential: string) => void | Promise<void>
  onError?: (message: string) => void
  /** Optional className for the wrapper div (e.g. for grid layout). */
  className?: string
}

/**
 * Renders a custom-styled Google Sign-In that matches the Login secondary button style.
 * Uses the official Google button underneath (invisible) so functionality is unchanged.
 */
export function GoogleSignInButton({ onSuccess, onError, className = '' }: GoogleSignInButtonProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const divRef = useRef<HTMLDivElement>(null)
  const callbackRef = useRef<(credential: string) => void>()
  callbackRef.current = async (credential: string) => {
    try {
      await onSuccess(credential)
    } catch (err) {
      onError?.(err instanceof Error ? err.message : 'Google sign-in failed')
    }
  }

  useEffect(() => {
    const container = containerRef.current
    const buttonEl = divRef.current
    if (!clientId || !buttonEl || !container) return

    const init = () => {
      if (!window.google?.accounts?.id) return false
      window.google.accounts.id.initialize({
        client_id: clientId,
        callback: (response: { credential: string }) => {
          callbackRef.current?.(response.credential)
        },
      })
      const width = container.offsetWidth || 280
      window.google.accounts.id.renderButton(buttonEl, {
        type: 'standard',
        theme: 'filled_black',
        size: 'large',
        text: 'continue_with',
        width,
      })
      return true
    }

    if (init()) return
    const id = setInterval(() => {
      if (init()) clearInterval(id)
    }, 100)
    return () => clearInterval(id)
  }, [])

  if (!clientId) {
    return <div className={`${buttonClass} ${className}`.trim()} aria-hidden />
  }

  return (
    <div
      ref={containerRef}
      className={`${buttonClass} relative ${className}`.trim()}
      style={{ minHeight: 40 }}
    >
      {/* Visible label matching Facebook button style */}
      <span className="pointer-events-none absolute inset-0 flex items-center justify-center gap-2 text-white">
        <svg className="h-4 w-4" viewBox="0 0 24 24" aria-hidden>
          <path
            fill="currentColor"
            d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
          />
          <path
            fill="currentColor"
            d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
          />
          <path
            fill="currentColor"
            d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
          />
          <path
            fill="currentColor"
            d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
          />
        </svg>
        <span>Google</span>
      </span>
      {/* Invisible Google button captures clicks and triggers sign-in */}
      <div
        ref={divRef}
        className="absolute inset-0 opacity-0 [&>iframe]:!w-full [&>iframe]:!h-full [&>iframe]:!min-h-full"
        aria-hidden
      />
    </div>
  )
}
